import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { ApiError } from "@utils/httpClient";
import { ReviewStatus } from "@models/job";
import type { SearchedMatch } from "@models/job";
import type { TmVariant } from "@models/mappingResult";
import type { SearchHit } from "@models/search";
import { searchApi } from "@services/searchApi";
import type { UseJobResultsState } from "@hooks/useJobResults.types";

export interface ReviewActions {
  handleAccept: (rowIndex: number, text?: string) => void;
  handleReject: (rowIndex: number) => void;
  handleRecover: (rowIndex: number) => void;
  /** Accepts every given row in one batched save, each sealing its current
   *  pick exactly as a single-row accept does. */
  handleAcceptAll: (rowIndexes: number[]) => Promise<void>;
  handleRejectAll: (rowIndexes: number[]) => Promise<void>;
  handleAddRow: (
    itemMpId: number,
    itemMpText: string,
    value: string,
    variant?: TmVariant | null,
  ) => Promise<void>;
}

/** Drop hits with no catalogue value — they carry nothing a row can display. */
const toSearchedMatch = (hit: SearchHit): SearchedMatch[] => {
  const valueDisplay = hit.metadata?.value_display;
  if (!valueDisplay) return [];
  return [
    {
      key: hit.id,
      // Forwarded as-is, `null` included: an unmeasured hit must stay
      // unmeasured all the way to the row. Coercing it to 0 (or to any
      // stand-in) is how "we did not measure this" became "this is a bad
      // match" on screen.
      similarity: hit.similarity,
      raw_similarity: hit.raw_similarity ?? null,
      score_basis: hit.score_basis ?? null,
      value_display: valueDisplay,
      quantity_id: hit.metadata?.quantity_id ?? null,
      value_id: hit.metadata?.value_id ?? null,
      exact_value_match: hit.metadata?.exact_value_match ?? null,
    },
  ];
};

/**
 * Row-level review handlers (accept / reject / correct / recover / add),
 * each translating a user action into a `results` edit. `handleAddRow`
 * surfaces success and failure toasts.
 *
 * `indexId` is the job's plugin id — the vector index the added row's value is
 * searched against on `xplatform_search_service`.
 */
export const useReviewActions = (
  results: UseJobResultsState,
  indexId?: string,
): ReviewActions => {
  const { t } = useTranslation();

  const handleAccept = useCallback(
    (rowIndex: number, text?: string): void => {
      // Accept SEALS the row's current pick: a previously picked alternative
      // (pending edit or already-saved chosen_rank) rides along explicitly so
      // the backend accepts THAT candidate — not the original top match. The
      // backend also preserves a stored pick when the rank is omitted, but
      // sending it closes the pick-PATCH-still-in-flight race.
      const chosenRank =
        results.edits.get(rowIndex)?.chosenRank ??
        results.rows.find((r) => r.rowIndex === rowIndex)?.chosenRank;
      results.applyEdit(rowIndex, {
        reviewStatus: ReviewStatus.Accepted,
        ...(chosenRank != null ? { chosenRank } : {}),
        ...(text !== undefined ? { text } : {}),
      });
    },
    [results],
  );

  const handleReject = useCallback(
    (rowIndex: number): void => {
      results.applyEdit(rowIndex, { reviewStatus: ReviewStatus.Rejected });
    },
    [results],
  );

  const handleRecover = useCallback(
    (rowIndex: number): void => {
      results.applyEdit(rowIndex, { reviewStatus: ReviewStatus.Pending });
    },
    [results],
  );

  const handleAcceptAll = useCallback(
    async (rowIndexes: number[]): Promise<void> => {
      await results.bulkApplyEdits(
        rowIndexes.map((rowIndex) => {
          const chosenRank =
            results.edits.get(rowIndex)?.chosenRank ??
            results.rows.find((r) => r.rowIndex === rowIndex)?.chosenRank;
          return {
            rowIndex,
            edit: {
              reviewStatus: ReviewStatus.Accepted,
              ...(chosenRank != null ? { chosenRank } : {}),
            },
          };
        }),
      );
    },
    [results],
  );

  const handleRejectAll = useCallback(
    async (rowIndexes: number[]): Promise<void> => {
      await results.bulkApplyEdits(
        rowIndexes.map((rowIndex) => ({
          rowIndex,
          edit: { reviewStatus: ReviewStatus.Rejected },
        })),
      );
    },
    [results],
  );

  const handleAddRow = useCallback(
    async (
      itemMpId: number,
      itemMpText: string,
      value: string,
      variant?: TmVariant | null,
    ): Promise<void> => {
      try {
        // The value search only enriches the new row with candidate matches.
        // A search outage must degrade to a row with none, never block the add.
        // `degraded` rides along so the server can tell "the catalogue does not
        // hold this value" from "the search that would have told us fell back";
        // an outage here is the strongest form of that — no evidence at all.
        const response = indexId
          ? await searchApi
              .searchValues({ indexId, itemMpId, value })
              .catch(() => ({ hits: [], degraded: ["search_unavailable"] }))
          : { hits: [], degraded: ["search_unavailable"] };
        await results.addRow(itemMpId, value, {
          itemMpText,
          matches: response.hits.flatMap(toSearchedMatch),
          degraded: response.degraded ?? [],
          ...(variant ? { variant } : {}),
        });
        notifications.show({
          color: "green",
          message: t("jobResultsPage.addRow.added", {
            defaultValue: "Row added",
          }),
        });
      } catch (err) {
        notifications.show({
          color: "red",
          message:
            err instanceof ApiError
              ? err.message
              : t("jobResultsPage.addRow.failed", {
                  defaultValue: "Could not add row",
                }),
        });
        throw err;
      }
    },
    [results, indexId, t],
  );

  return {
    handleAccept,
    handleReject,
    handleRecover,
    handleAcceptAll,
    handleRejectAll,
    handleAddRow,
  };
};
